import React from "react";
import { Lock, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * LockedOverlay — wraps premium content with a blurred teaser gate.
 *
 * Props:
 *   hasPro    {boolean}  — if true, renders children normally
 *   label     {string}   — short name shown on the lock badge (e.g. "Constraints")
 *   children  {node}     — the premium content to render (blurred when locked)
 *   compact   {boolean}  — smaller overlay for tight spaces
 */
const LockedOverlay = ({ hasPro, label = "Pro Feature", children, compact = false }) => {
    const navigate = useNavigate();

    if (hasPro) return <>{children}</>;

    return (
        <div className="relative overflow-hidden rounded-2xl">
            {/* Blurred content — visible but unreadable */}
            <div
                className="pointer-events-none select-none"
                style={{ filter: "blur(6px)", opacity: 0.55 }}
                aria-hidden="true"
            >
                {children}
            </div>

            {/* Lock overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-sm rounded-2xl z-10">
                <div className={`flex flex-col items-center gap-${compact ? "2" : "3"} text-center px-4`}>
                    <div className={`${compact ? "w-9 h-9" : "w-12 h-12"} rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg`}>
                        <Lock className={`${compact ? "w-4 h-4" : "w-6 h-6"} text-white`} />
                    </div>
                    <div>
                        <p className={`font-black text-slate-800 ${compact ? "text-xs" : "text-sm"}`}>
                            {label}
                        </p>
                        <p className={`text-slate-500 ${compact ? "text-[10px]" : "text-xs"} mt-0.5`}>
                            Pro feature — upgrade to unlock
                        </p>
                    </div>
                    <button
                        onClick={() => navigate("/#subscription-plans")}
                        className={`flex items-center gap-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all ${compact ? "px-3 py-1.5 text-[10px]" : "px-4 py-2 text-xs"}`}
                    >
                        <Sparkles className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
                        Upgrade to Pro
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LockedOverlay;
